import React, { useEffect, useRef, useState } from "react";
import "./OrderNote.css";

const MAX_NOTE_LENGTH = 150;
// One-tap phrases: phones are slow to type on, and these cover most requests.
const QUICK_NOTES = ["Less sugar", "Extra hot", "Less ice", "Less spicy", "Warm it up"];

const splitNote = (text) => text.split(",").map((part) => part.trim()).filter(Boolean);

const NoteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 4.5h14a1.5 1.5 0 0 1 1.5 1.5v9.5a1.5 1.5 0 0 1-1.5 1.5H11l-4.5 3.5V17H5A1.5 1.5 0 0 1 3.5 15.5V6A1.5 1.5 0 0 1 5 4.5Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <path d="M7.5 9h9M7.5 12.2h5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

/**
 * "Note for the barista" — optional, collapsed by default so it adds no friction.
 * Typing stays in a local draft and is handed to the cart only on Done / blur, so
 * writing a note never triggers a cart save by itself; it rides along with the
 * next real save. `onEditingChange` lets the cart page give the keyboard the room.
 */
export default function OrderNote({ value = "", onCommit, onEditingChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);
  const cardRef = useRef(null);

  // Follow outside changes (e.g. the cart being cleared) while the editor is closed.
  useEffect(() => {
    if (!open) setDraft(value);
  }, [value, open]);

  useEffect(() => {
    onEditingChange?.(open);
    return () => onEditingChange?.(false);
  }, [open, onEditingChange]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    cardRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [open]);

  const commit = () => {
    const next = draft.replace(/\s+/g, " ").trim().slice(0, MAX_NOTE_LENGTH);
    setDraft(next);
    if (next !== value) onCommit?.(next);
  };

  const done = () => {
    commit();
    setOpen(false);
  };

  const activeChips = splitNote(draft).map((part) => part.toLowerCase());
  const toggleChip = (text) => {
    const parts = splitNote(draft);
    const at = parts.findIndex((part) => part.toLowerCase() === text.toLowerCase());
    if (at >= 0) parts.splice(at, 1);
    else parts.push(text);
    const next = parts.join(", ");
    if (next.length <= MAX_NOTE_LENGTH) setDraft(next);
  };

  if (!open) {
    const hasNote = Boolean(value);
    return (
      <button
        type="button"
        className={`order-note-row ${hasNote ? "filled" : ""}`}
        onClick={() => setOpen(true)}
        aria-label={hasNote ? "Edit note for the barista" : "Add a note for the barista"}
      >
        <span className="order-note-icon"><NoteIcon /></span>
        <span className="order-note-text">
          <span className="order-note-title">
            {hasNote ? "Note for the barista" : "Add a note for the barista"}
          </span>
          {hasNote ? (
            <span className="order-note-preview">{value}</span>
          ) : (
            <span className="order-note-sub">Less sugar, extra hot… (optional)</span>
          )}
        </span>
        <span className="order-note-action">{hasNote ? "Edit" : "Add"}</span>
      </button>
    );
  }

  return (
    <div className="order-note-card" ref={cardRef}>
      <div className="order-note-head">
        <span className="order-note-icon"><NoteIcon /></span>
        <span className="order-note-title">Note for the barista</span>
        <span className="order-note-optional">Optional</span>
      </div>
      <textarea
        ref={inputRef}
        className="order-note-input"
        rows={3}
        maxLength={MAX_NOTE_LENGTH}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        placeholder="e.g. Less sugar, extra hot, no ice…"
        enterKeyHint="done"
        aria-label="Note for the barista"
      />
      <div className="order-note-chips" role="group" aria-label="Quick notes">
        {QUICK_NOTES.map((text) => (
          <button
            key={text}
            type="button"
            className={`order-note-chip ${activeChips.includes(text.toLowerCase()) ? "on" : ""}`}
            // keep the keyboard open while tapping a chip
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => toggleChip(text)}
            aria-pressed={activeChips.includes(text.toLowerCase())}
          >
            {text}
          </button>
        ))}
      </div>
      <div className="order-note-foot">
        <span className="order-note-count">{draft.length}/{MAX_NOTE_LENGTH}</span>
        <button type="button" className="order-note-done" onMouseDown={(e) => e.preventDefault()} onClick={done}>
          Done
        </button>
      </div>
    </div>
  );
}
